import type {
  ActivityDonutItem,
  DashboardTimeFilter,
  DashboardTrend,
  UserGrowthChartPoint,
} from "../types/dashboard-ui.types";
import type {
  DashboardStatisticResponse,
  OwnCreatedDataResponse,
  UserActivityDataResponse,
  UserGrowthDataResponse,
} from "../types/dashboard-data.types";
import { calculateSetupStatus, getMissingItems, type SetupStatus } from "./setupStatus";
import { DashboardCalculator } from "./dashboardCalculations";

export type StatisticKey =
  | "total_users"
  | "new_users_this_week"
  | "active_events"
  | "active_surveys"
  | "active_coupons"
  | "total_community_partners";

export interface DashboardStatValue {
  key: StatisticKey;
  value: number;
}

export const STATISTIC_KEYS: readonly StatisticKey[] = [
  "total_users",
  "new_users_this_week",
  "active_events",
  "active_surveys",
  "active_coupons",
  "total_community_partners",
];

export const getDashboardStatValues = (
  data: DashboardStatisticResponse | null,
): DashboardStatValue[] => STATISTIC_KEYS.map((key) => ({ key, value: data?.[key] ?? 0 }));

export const areDashboardStatsEmpty = (data: DashboardStatisticResponse | null): boolean =>
  getDashboardStatValues(data).every((stat) => stat.value === 0);

export const getDashboardTrend = (data: UserGrowthDataResponse | null): DashboardTrend => {
  if (!data) return undefined;
  return new DashboardCalculator(data).getTrend();
};

export const buildUserGrowthChartPoints = (
  data: UserGrowthDataResponse | null,
  timeFilter: DashboardTimeFilter,
): UserGrowthChartPoint[] => {
  if (!data) return [];
  return new DashboardCalculator(data).getChartDataByFilter(timeFilter).map((item) => ({
    date: item.date,
    users: item.users,
    new: item.new,
    change: item.change,
  }));
};

export interface ActivityDonutLabels {
  activeThisWeek: string;
  activeToday: string;
  inactive: string;
}

export const buildActivityDonutItems = (
  data: UserActivityDataResponse | null,
  labels: ActivityDonutLabels,
): ActivityDonutItem[] => {
  if (!data) return [];
  return [
    {
      name: labels.activeThisWeek,
      key: "active_this_week",
      value: data.active_this_week?.count ?? 0,
    },
    {
      name: labels.activeToday,
      key: "active_today",
      value: data.active_today?.count ?? 0,
    },
    {
      name: labels.inactive,
      key: "inactive",
      value: data.inactive_this_week?.count ?? 0,
    },
  ];
};

export interface DashboardSetupView {
  status: SetupStatus;
  missingItemIds: string[];
}

export const getDashboardSetupView = (
  data: OwnCreatedDataResponse | null,
): DashboardSetupView | null => {
  if (!data) return null;
  return {
    status: calculateSetupStatus(data),
    missingItemIds: getMissingItems(data),
  };
};
